/**
 * La tarjeta de los primeros pasos, en Inicio · 03-11-2026
 *
 * Lo hecho, en una línea con su marca; lo que falta, como las tarjetas de lo
 * pendiente, con el botón que lleva a hacerlo. **El siguiente se destaca**: es el
 * que hay que hacer ahora, y los de después a veces dependen de él (un grupo pide
 * una sede).
 *
 * Se puede ocultar —un club que cobra fuera de Kiap no va a poner tarifas nunca, y
 * una guía que no se va es ruido—. Se recuerda en este navegador y por club: es una
 * comodidad de la pantalla, no un dato del club. Si `localStorage` falla (ventana
 * privada, almacenamiento bloqueado), simplemente vuelve a salir.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Icono } from '@/shared/ui/Icono';
import { progreso, siguiente, type PasoInicial } from '@/club/inicio/pasos';

const clave = (club: string) => `kiap.primeros-pasos.oculto.${club}`;

function leerOculto(club: string): boolean {
  try {
    return localStorage.getItem(clave(club)) === '1';
  } catch {
    return false;
  }
}

function guardarOculto(club: string) {
  try {
    localStorage.setItem(clave(club), '1');
  } catch {
    // Sin almacenamiento, se oculta en esta visita y vuelve en la siguiente.
  }
}

export function PrimerosPasos({
  club,
  pasos,
}: {
  club: string;
  pasos: readonly PasoInicial[];
}) {
  const navegar = useNavigate();
  const [oculto, setOculto] = useState(() => leerOculto(club));
  const ahora = siguiente(pasos);

  if (oculto || !ahora) return null;

  const { hechos, total } = progreso(pasos);

  return (
    <section className="primeros-pasos" aria-labelledby="primeros-pasos">
      <h2 className="seccion" id="primeros-pasos">
        Primeros pasos
        <span className="seccion-numero">
          {hechos} de {total}
        </span>
      </h2>

      <ol className="pendientes pasos">
        {pasos.map((p) =>
          p.hecho ? (
            <li key={p.clave} className="paso-hecho">
              <Icono nombre="hecho" tamano={18} />
              <span>{p.titulo}</span>
            </li>
          ) : (
            <li key={p.clave}>
              <button
                type="button"
                className={`pendiente${p === ahora ? ' pendiente-low paso-siguiente' : ''}`}
                aria-current={p === ahora ? 'step' : undefined}
                onClick={() => void navegar({ href: p.destino })}
              >
                <span className="pendiente-icono" aria-hidden="true">
                  <Icono nombre={p.icono} tamano={20} />
                </span>
                <span className="pendiente-texto">
                  <strong>{p.titulo}</strong>
                  <span>{p.detalle}</span>
                </span>
                <span className="pendiente-accion">
                  <span className="pendiente-verbo">{p.accion}</span>
                  <Icono nombre="flecha" tamano={16} />
                </span>
              </button>
            </li>
          )
        )}
      </ol>

      <p className="primeros-pasos-pie">
        <button
          type="button"
          className="btn-plano"
          onClick={() => {
            guardarOculto(club);
            setOculto(true);
          }}
        >
          Ocultar esta guía
        </button>
      </p>
    </section>
  );
}
